/**
 * Keeping the installed-capacity field describing the module that is selected.
 *
 * Once a design is saved, the plant's size is a fact: module count times the
 * module's STC rating. The capacity field stops being a target the
 * auto-designer aims at and becomes a statement about the design. Swapping the
 * module without re-running the layout used to leave that statement behind,
 * still describing the previous module - an 1,800-module plant kept reading
 * 1,062 kWp after a 590 W module became a 500 W one, when it was 900 kWp.
 *
 * Nothing downstream caught it, because the engine sizes from the design and
 * never reads the field. The stale number surfaced only on screen, in the
 * estimate fingerprint, and in monitoring, which falls back to it as the DC
 * rating behind every performance ratio it computes.
 */

/** A finite, positive number, or null. */
function positive(value) {
  const out = Number(value);
  return Number.isFinite(out) && out > 0 ? out : null;
}

/**
 * The capacity the saved design actually has, in kW, or null when there is not
 * enough to compute one (no design yet, or no rating for the module).
 *
 * @param {number|string} moduleCount modules in the saved design
 * @param {number|string} moduleKw    the module's STC rating, in kW
 */
export function capacityFromDesign(moduleCount, moduleKw) {
  const count = positive(moduleCount);
  const rating = positive(moduleKw);
  if (count === null || rating === null) return null;
  return count * rating;
}

/**
 * Half a watt per module, floored at 10 W.
 *
 * The field is stored to two decimals of a kW and module ratings are not
 * always whole watts, so an exact comparison would rewrite the field - and
 * push an undo step - on rounding alone. Scaling with the module count keeps
 * the allowance proportional on a large plant without letting it grow into
 * anything that could hide a real swap: the smallest module change anyone
 * makes moves the capacity by more than a tenth.
 */
export function capacityTolerance(moduleCount) {
  const count = positive(moduleCount) || 0;
  return Math.max(0.01, count * 0.0005);
}

/**
 * Should the stored capacity be replaced by the design's own?
 *
 * False whenever there is nothing to compare: with no design the field IS the
 * target the auto-designer sizes to, and a user who types "1,000 kWp" before
 * designing anything must not have it overwritten.
 */
export function capacityNeedsUpdate(storedKw, moduleCount, moduleKw) {
  const designKw = capacityFromDesign(moduleCount, moduleKw);
  if (designKw === null) return false;
  const stored = Number(storedKw);
  if (!Number.isFinite(stored)) return true;
  return Math.abs(stored - designKw) > capacityTolerance(moduleCount);
}

/** The value to write into the field, formatted as the application stores it. */
export function formatCapacityKw(designKw) {
  const value = positive(designKw);
  return value === null ? null : value.toFixed(2);
}
